import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { GraduationCap, User, Lock, BookOpen, Users, School, ArrowRight, Loader2, Phone, Calendar, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const StudentRegister = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { registerStudent } = useAuth();
  const [loading, setLoading] = useState(false);
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [validatingTeacher, setValidatingTeacher] = useState(false);
  const [teacherError, setTeacherError] = useState(null);
  
  const [formData, setFormData] = useState({
    first_name: '',
    second_name: '',
    third_name: '',
    family_name: '',
    username: '',
    password: '',
    confirmPassword: '',
    date_of_birth: '',
    phone_number: '',
    school: '',
    grade: '',
    section: '',
    teacher_code: '',
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const grades = [
    'الصف الأول',
    'الصف الثاني',
    'الصف الثالث',
    'الصف الرابع',
    'الصف الخامس',
    'الصف السادس',
    'الصف السابع',
    'الصف الثامن',
    'الصف التاسع',
    'الصف العاشر',
    'الصف الحادي عشر',
    'الصف الثاني عشر',
    'غير ذلك',
  ];

  const sections = ['أ', 'ب', 'ج', 'د', 'هـ', 'و'];

  useEffect(() => {
    const teacherCode = searchParams.get('teacher');
    if (teacherCode) {
      setFormData(prev => ({ ...prev, teacher_code: teacherCode.toUpperCase() }));
      validateTeacherCode(teacherCode);
    }
  }, [searchParams]);

  const validateTeacherCode = async (code) => {
    if (!code || code.length < 4) {
      setTeacherInfo(null);
      setTeacherError(null);
      return;
    }

    setValidatingTeacher(true);
    setTeacherError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/public/teacher/${code.toUpperCase()}`);
      if (response.ok) {
        const data = await response.json();
        setTeacherInfo(data);
        setTeacherError(null);
      } else {
        setTeacherInfo(null);
        setTeacherError('رمز المعلم غير صالح');
      }
    } catch (error) {
      console.error('Error validating teacher code:', error);
      setTeacherInfo(null);
      setTeacherError('فشل التحقق من رمز المعلم');
    }
    
    setValidatingTeacher(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'username') {
      const cleanedValue = value.toLowerCase().replace(/\s/g, '').replace(/[^a-z0-9._]/g, '');
      setFormData({
        ...formData,
        [name]: cleanedValue,
      });
    } else if (name === 'teacher_code') {
      const upperValue = value.toUpperCase();
      setFormData({
        ...formData,
        [name]: upperValue,
      });
      if (upperValue.length >= 4) {
        validateTeacherCode(upperValue);
      } else {
        setTeacherInfo(null);
        setTeacherError(null);
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.second_name || !formData.third_name || !formData.family_name) {
      toast.error('يرجى إدخال الاسم الرباعي كاملاً');
      return;
    }

    if (!formData.username || !formData.password) {
      toast.error('يرجى إدخال اسم المستخدم وكلمة المرور');
      return;
    }

    if (!formData.date_of_birth) {
      toast.error('يرجى إدخال تاريخ الميلاد');
      return;
    }

    if (!formData.phone_number) {
      toast.error('يرجى إدخال رقم الهاتف');
      return;
    }

    if (!formData.school) {
      toast.error('يرجى إدخال اسم المدرسة');
      return;
    }

    if (!formData.grade || !formData.section) {
      toast.error('يرجى اختيار الصف والشعبة');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('كلمة المرور غير متطابقة');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    if (formData.username.length < 4) {
      toast.error('اسم المستخدم يجب أن يكون 4 أحرف على الأقل');
      return;
    }

    if (formData.teacher_code && !teacherInfo) {
      toast.error('رمز المعلم غير صالح');
      return;
    }

    setLoading(true);
    
    const submitData = {
      ...formData,
      teacher_code: formData.teacher_code || null
    };
    
    const result = await registerStudent(submitData);
    setLoading(false);

    if (result.success) {
      toast.success('تم استلام طلب التسجيل بنجاح');
      navigate('/student/pending');
    } else {
      toast.error(result.error || 'فشل التسجيل');
    }
  };

  const isTeacherLocked = searchParams.get('teacher') && teacherInfo;

  return (
    <div className="min-h-screen bg-[#FFFFFF] flex flex-col">
      <main className="flex-1 flex">
        {/* Right Side - Logo Background with Blur */}
        <div className="hidden lg:flex w-1/2 relative overflow-hidden bg-[#FDF8F0]">
          {/* Blurred Logo Background */}
          <div className="absolute inset-0">
            <img src="/logo.png" alt="background" className="w-full h-full object-cover scale-110 blur-sm opacity-30" />
          </div>
          
          {/* Light Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#FDF8F0]/90 via-[#FDF8F0]/70 to-[#FDF8F0]/90"></div>
          
          {/* Amber Glow Effects */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(239,159,39,0.15),transparent_70%)]"></div>

          <div className="relative z-10 flex flex-col items-center justify-center w-full text-center space-y-8 p-8">
            <div className="w-48 h-48 rounded-3xl overflow-hidden border-2 border-[#E8DFCF] shadow-[0_0_60px_rgba(239,159,39,0.3)]">
              <img src="/logo.png" alt="Logo" className="w-full h-full object-cover" />
            </div>
            <h1 className="text-5xl font-extrabold text-[#412402] drop-shadow-sm">المعتمد في اللغة العربية</h1>
            <p className="text-[#6B4C2A] text-xl">منصة تعليمية متخصصة في اللغة العربية</p>
            <div className="w-40 h-1 bg-gradient-to-r from-transparent via-[#EF9F27] to-transparent"></div>
          </div>
        </div>

        {/* Left Side - Form */}
        <div className="w-full lg:w-1/2 relative overflow-hidden flex items-center justify-center p-8 bg-[#FFFFFF]">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_30%,rgba(239,159,39,0.05),transparent_60%)]"></div>

          <Card className="relative z-10 w-full max-w-2xl bg-[#FDF8F0] border border-[#E8DFCF] shadow-xl">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 bg-[#EF9F27]/10 border border-[#E8DFCF] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-[#EF9F27]" />
              </div>
              <CardTitle className="text-2xl font-bold text-[#412402]">
                تسجيل طالب جديد
              </CardTitle>
              
              {teacherInfo && (
                <div className="mt-4 bg-[#EF9F27]/10 border border-[#EF9F27]/30 rounded-lg p-3">
                  <div className="flex items-center justify-center gap-2 text-[#854F0B]">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">التسجيل تحت إشراف المعلم: {teacherInfo.teacher_name}</span>
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Four-part Name Section */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name" className="text-[#412402]">الاسم الأول</Label>
                      <Input
                        id="first_name"
                        name="first_name"
                        type="text"
                        placeholder="الاسم الأول"
                        value={formData.first_name}
                        onChange={handleChange}
                        className="bg-[#FFFFFF] border-[#E8DFCF] text-[#412402] placeholder:text-[#8B7355] focus:border-[#EF9F27]"
                        data-testid="student-first-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="second_name" className="text-[#412402]">اسم الأب</Label>
                      <Input
                        id="second_name"
                        name="second_name"
                        type="text"
                        placeholder="اسم الأب"
                        value={formData.second_name}
                        onChange={handleChange}
                        className="bg-[#FFFFFF] border-[#E8DFCF] text-[#412402] placeholder:text-[#8B7355] focus:border-[#EF9F27]"
                        data-testid="student-second-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="third_name" className="text-[#412402]">اسم الجد</Label>
                      <Input
                        id="third_name"
                        name="third_name"
                        type="text"
                        placeholder="اسم الجد"
                        value={formData.third_name}
                        onChange={handleChange}
                        className="bg-[#FFFFFF] border-[#E8DFCF] text-[#412402] placeholder:text-[#8B7355] focus:border-[#EF9F27]"
                        data-testid="student-third-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="family_name" className="text-[#412402]">اسم العائلة</Label>
                      <Input
                        id="family_name"
                        name="family_name"
                        type="text"
                        placeholder="اسم العائلة"
                        value={formData.family_name}
                        onChange={handleChange}
                        className="bg-[#FFFFFF] border-[#E8DFCF] text-[#412402] placeholder:text-[#8B7355] focus:border-[#EF9F27]"
                        data-testid="student-family-name-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Personal Info Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-[#412402] border-b border-[#E8DFCF] pb-2">المعلومات الشخصية *</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="date_of_birth" className="text-[#412402]">تاريخ الميلاد</Label>
                      <div className="relative">
                        <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8B7355]" />
                        <Input
                          id="date_of_birth"
                          name="date_of_birth"
                          type="date"
                          value={formData.date_of_birth}
                          onChange={handleChange}
                          className="pr-10 bg-[#FFFFFF] border-[#E8DFCF] text-[#412402] focus:border-[#EF9F27]"
                          data-testid="student-dob-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone_number" className="text-[#412402]">رقم الهاتف</Label>
                      <div className="relative">
                        <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8B7355]" />
                        <Input
                          id="phone_number"
                          name="phone_number"
                          type="tel"
                          placeholder="07xxxxxxxx"
                          value={formData.phone_number}
                          onChange={handleChange}
                          className="pr-10 bg-[#FFFFFF] border-[#E8DFCF] text-[#412402] placeholder:text-[#8B7355] focus:border-[#EF9F27]"
                          dir="ltr"
                          data-testid="student-phone-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* School Info Section */}
                <div className="space-y-4">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="school" className="text-[#412402]">اسم المدرسة</Label>
                      <div className="relative">
                        <School className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8B7355]" />
                        <Input
                          id="school"
                          name="school"
                          type="text"
                          placeholder="اسم المدرسة"
                          value={formData.school}
                          onChange={handleChange}
                          className="pr-10 bg-[#FFFFFF] border-[#E8DFCF] text-[#412402] placeholder:text-[#8B7355] focus:border-[#EF9F27]"
                          data-testid="student-school-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[#412402]">الصف</Label>
                        <Select
                          value={formData.grade}
                          onValueChange={(value) => setFormData({ ...formData, grade: value })}
                        >
                          <SelectTrigger className="bg-[#FFFFFF] border-[#E8DFCF] text-[#412402] focus:border-[#EF9F27]" data-testid="student-grade-select">
                            <BookOpen className="w-4 h-4 ml-2 text-[#8B7355]" />
                            <SelectValue placeholder="اختر الصف" />
                          </SelectTrigger>
                          <SelectContent>
                            {grades.map((grade) => (
                              <SelectItem key={grade} value={grade}>
                                {grade}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#412402]">الشعبة</Label>
                        <Select
                          value={formData.section}
                          onValueChange={(value) => setFormData({ ...formData, section: value })}
                        >
                          <SelectTrigger className="bg-[#FFFFFF] border-[#E8DFCF] text-[#412402] focus:border-[#EF9F27]" data-testid="student-section-select">
                            <Users className="w-4 h-4 ml-2 text-[#8B7355]" />
                            <SelectValue placeholder="اختر الشعبة" />
                          </SelectTrigger>
                          <SelectContent>
                            {sections.map((section) => (
                              <SelectItem key={section} value={section}>
                                شعبة {section}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Info Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-[#412402] border-b border-[#E8DFCF] pb-2">معلومات الحساب *</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="username" className="text-[#412402]">اسم المستخدم</Label>
                      <div className="flex items-center gap-0">
                        <div className="relative flex-1">
                          <User className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8B7355]" />
                          <Input
                            id="username"
                            name="username"
                            type="text"
                            placeholder="اسم_المستخدم"
                            value={formData.username}
                            onChange={handleChange}
                            className="pr-10 rounded-l-none border-l-0 bg-[#FFFFFF] border-[#E8DFCF] text-[#412402] placeholder:text-[#8B7355] focus:border-[#EF9F27]"
                            dir="ltr"
                            data-testid="student-username-input"
                          />
                        </div>
                        <div className="bg-[#FDF8F0] border border-[#E8DFCF] border-r-0 rounded-l-lg px-3 py-2 text-sm text-[#6B4C2A] whitespace-nowrap">
                          @school.com
                        </div>
                      </div>
                      <p className="text-xs text-[#8B7355]">سيكون اسم المستخدم الكامل: {formData.username || 'username'}@school.com</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="password" className="text-[#412402]">كلمة المرور</Label>
                        <div className="relative">
                          <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8B7355]" />
                          <Input
                            id="password"
                            name="password"
                            type="password"
                            placeholder="********"
                            value={formData.password}
                            onChange={handleChange}
                            className="pr-10 bg-[#FFFFFF] border-[#E8DFCF] text-[#412402] placeholder:text-[#8B7355] focus:border-[#EF9F27]"
                            data-testid="student-password-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword" className="text-[#412402]">تأكيد كلمة المرور</Label>
                        <div className="relative">
                          <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8B7355]" />
                          <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            type="password"
                            placeholder="********"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            className="pr-10 bg-[#FFFFFF] border-[#E8DFCF] text-[#412402] placeholder:text-[#8B7355] focus:border-[#EF9F27]"
                            data-testid="student-confirm-password-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#EF9F27] hover:bg-[#D88E1F] text-[#854F0B] h-12 text-base font-bold shadow-[0_4px_15px_rgba(239,159,39,0.3)]"
                  disabled={loading}
                  data-testid="student-register-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 ml-2 animate-spin" />
                      جاري التسجيل...
                    </>
                  ) : (
                    <>
                      إرسال طلب التسجيل
                      <ArrowRight className="w-5 h-5 mr-2" />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-[#6B4C2A]">
                  لديك حساب بالفعل؟{' '}
                  <Link 
                    to="/student/login" 
                    className="text-[#EF9F27] font-medium hover:underline"
                    data-testid="login-link"
                  >
                    تسجيل الدخول
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default StudentRegister;
